import type { OnLoadResult, PartialMessage, PartialNote, ResolveResult } from 'esbuild';
import { dirname, join, relative } from 'node:path';
import { fileURLToPath, pathToFileURL } from 'node:url';
import type { CompileResult, Exception, Syntax } from 'sass-embedded';
import { MemoryCache } from '../cache';
import type { SassCompiler } from '../sass/sass-service';
import { StylesheetLanguage, StylesheetPluginOptions } from './stylesheet-plugin-factory';

let sassService: SassCompiler | undefined;
let sassServicePromise: Promise<SassCompiler> | undefined;
let resolutionCache: MemoryCache<URL | null> | undefined;
let packageRootCache: MemoryCache<string | null> | undefined;

function isSassException(error: unknown): error is Exception {
  return !!error && typeof error === 'object' && 'sassMessage' in error;
}

export function resetSassWorkerPoolCaches(): void {
  resolutionCache?.clear();
  packageRootCache?.clear();
  if (sassService) {
    sassService.clearCache();
  } else if (sassServicePromise !== undefined) {
    void sassServicePromise.then(service => service.clearCache());
  }
}

export function shutdownSassWorkerPool(): void {
  resetSassWorkerPoolCaches();
  if (sassService) {
    void sassService.close();
    sassService = undefined;
  } else if (sassServicePromise !== undefined) {
    void sassServicePromise.then(shutdownSassWorkerPool);
  }
  sassServicePromise = undefined;
}

export const SassStylesheetLanguage = Object.freeze<StylesheetLanguage>({
  name: 'sass',
  componentFilter: /^s[ac]ss;/,
  fileFilter: /\.s[ac]ss$/,
  process(data, file, format, options, build) {
    const syntax = format === 'sass' ? 'indented' : 'scss';
    const resolveUrl = async (url: string, resolveDir: string | undefined) => {
      const path = url.startsWith('pkg:') ? url.slice(4) : url;
      const result = await build.resolve(path, {
        kind: 'import-rule',
        resolveDir,
      });

      return result;
    };

    return compileString(data, file, syntax, options, resolveUrl, build.initialOptions.absWorkingDir);
  },
});

export function isPackageUrl(url: string): boolean {
  if (url.startsWith('pkg:')) {
    return true;
  }

  return url.length > 0 && !url.startsWith('.') && !url.startsWith('/') && !url.startsWith('\\') && !url.includes(':');
}

function parsePackageName(url: string): { packageName: string; readonly pathSegments: string[] } {
  const parts = (url.startsWith('pkg:') ? url.slice(4) : url).split('/');
  const hasScope = parts.length >= 2 && parts[0][0] === '@';
  const [nameOrScope, nameOrFirstPath, ...pathPart] = parts;
  const packageName = hasScope ? `${nameOrScope}/${nameOrFirstPath}` : nameOrScope;

  return {
    packageName,
    get pathSegments() {
      return !hasScope && nameOrFirstPath ? [nameOrFirstPath, ...pathPart] : pathPart;
    },
  };
}

/**
 * Returns the scope of the package resolutions of a stylesheet. A stylesheet within `node_modules`
 * is scoped to the root of its package, which follows the last `node_modules` segment of its path.
 * Every file of a package resolves packages against the same `node_modules` directories, while a
 * nested dependency has its own. All other stylesheets share the working directory, allowing
 * component stylesheets to share package resolutions.
 */
export function getPackageScope(containingPath: string | undefined, workingDirectory: string | undefined): string {
  // The directory segments of the stylesheet, excluding the file name
  const segments = containingPath?.split(/[\\/]/).slice(0, -1) ?? [];
  const index = segments.lastIndexOf('node_modules');
  if (index === -1) {
    return workingDirectory ?? '';
  }

  const packageNameLength = segments[index + 1]?.[0] === '@' ? 2 : 1;

  return segments.slice(0, index + 1 + packageNameLength).join('/');
}

async function compileString(
  data: string,
  filePath: string,
  syntax: Syntax,
  options: StylesheetPluginOptions,
  resolveUrl: (url: string, resolveDir: string | undefined) => Promise<ResolveResult>,
  workingDirectory: string | undefined,
): Promise<OnLoadResult> {
  // Lazily load Sass when a Sass file is found
  if (sassService === undefined) {
    if (sassServicePromise === undefined) {
      sassServicePromise = import('../sass/sass-service').then(sassService => new sassService.SassCompiler(true));
    }
    try {
      sassService = await sassServicePromise;
    } finally {
      sassServicePromise = undefined;
    }
  }

  // Caching follows Sass behavior where a given package url will always resolve to the same value
  // regardless of its importer's path, except for importers within `node_modules`, which are
  // scoped to the root of their package. Relative paths are qualified with the containing URL.
  // A null value indicates that the cached resolution attempt failed to find a location and
  // later stage resolution should be attempted. This avoids potentially expensive repeat
  // failing resolution attempts.
  resolutionCache ??= new MemoryCache<URL | null>();
  packageRootCache ??= new MemoryCache<string | null>();
  const currentResolutionCache = resolutionCache;
  const currentPackageRootCache = packageRootCache;
  const warnings: PartialMessage[] = [];
  const { silenceDeprecations, futureDeprecations, fatalDeprecations } = options.sass ?? {};

  try {
    const { css, sourceMap, loadedUrls } = await sassService.compileStringAsync(data, {
      url: pathToFileURL(filePath),
      style: 'expanded',
      syntax,
      loadPaths: options.includePaths,
      sourceMap: options.sourcemap,
      sourceMapIncludeSources: options.sourcemap,
      silenceDeprecations,
      fatalDeprecations,
      futureDeprecations,
      quietDeps: true,
      importers: [
        {
          findFileUrl: (url, options) => {
            const containingPath =
              options.containingUrl?.protocol === 'file:' ? fileURLToPath(options.containingUrl) : undefined;
            const resolveDir = containingPath ? dirname(containingPath) : workingDirectory;
            const isPackage = isPackageUrl(url);
            const scope = getPackageScope(containingPath, workingDirectory);
            const cacheKey = isPackage ? `${scope}:${url}` : `${options.containingUrl?.href ?? ''}:${url}`;

            return currentResolutionCache.getOrCreate(cacheKey, async () => {
              const result = await resolveUrl(url, resolveDir);
              if (result.path) {
                return pathToFileURL(result.path);
              }

              // Check for package deep imports
              if (!isPackage) {
                return null;
              }

              const { packageName, pathSegments } = parsePackageName(url);

              // Caching package root locations is particularly beneficial for `@material/*` packages
              // which extensively use deep imports.
              const packageRoot = await currentPackageRootCache.getOrCreate(`${scope}:${packageName}`, async () => {
                // Use the required presence of a package root `package.json` file to resolve the location
                const packageResult = await resolveUrl(packageName + '/package.json', resolveDir);

                return packageResult.path ? dirname(packageResult.path) : null;
              });

              // Package not found could be because of an error or the specifier is intended to be found
              // via a later stage of the resolution process (`loadPaths`, etc.).
              // Errors are reported after the full completion of the resolution process. Exceptions for
              // not found packages should not be raised here.
              if (packageRoot) {
                return pathToFileURL(join(packageRoot, ...pathSegments));
              }

              // Not found
              return null;
            });
          },
        },
      ],
      logger: {
        warn: (text, { deprecation, stack, span }) => {
          const notes: PartialNote[] = [];
          if (deprecation) {
            notes.push({ text });
          }
          if (stack && !span) {
            notes.push({ text: stack });
          }

          warnings.push({
            text: deprecation ? 'Deprecation' : text,
            location: span && {
              file: span.url && fileURLToPath(span.url),
              lineText: span.context,
              // Sass line numbers are 0-based while esbuild's are 1-based
              line: span.start.line + 1,
              column: span.start.column,
            },
            notes,
          });
        },
      },
    });

    return {
      loader: 'css',
      contents: sourceMap ? `${css}\n${sourceMapToUrlComment(sourceMap, dirname(filePath))}` : css,
      watchFiles: loadedUrls.map(url => fileURLToPath(url)),
      warnings,
    };
  } catch (error) {
    if (isSassException(error)) {
      const fileWithError = error.span.url ? fileURLToPath(error.span.url) : undefined;

      const watchFiles = [filePath, ...extractFilesFromStack(error.sassStack)];
      if (fileWithError) {
        watchFiles.push(fileWithError);
      }

      return {
        loader: 'css',
        errors: [
          {
            text: error.message,
          },
        ],
        warnings,
        watchFiles,
      };
    }

    throw error;
  }
}

function sourceMapToUrlComment(sourceMap: Exclude<CompileResult['sourceMap'], undefined>, root: string): string {
  // Remove `file` protocol from all sourcemap sources and adjust to be relative to the input file.
  // This allows esbuild to correctly process the paths.
  sourceMap.sources = sourceMap.sources.map(source => relative(root, fileURLToPath(source)));

  const urlSourceMap = Buffer.from(JSON.stringify(sourceMap), 'utf-8').toString('base64');

  return `/*# sourceMappingURL=data:application/json;charset=utf-8;base64,${urlSourceMap} */`;
}

function* extractFilesFromStack(stack: string): Iterable<string> {
  const lines = stack.split('\n');
  const cwd = process.cwd();

  // Stack line has format of "<file> <location> <identifier>"
  for (const line of lines) {
    const segments = line.split(' ');
    if (segments.length < 3) {
      break;
    }

    // Extract path from stack line.
    // Paths may contain spaces. All segments before location are part of the file path.
    let path = '';
    let index = 0;
    while (!segments[index].match(/\d+:\d+/)) {
      path += segments[index++];
    }

    if (path) {
      // Stack paths from sass are relative to the current working directory (not input file or workspace root)
      yield join(cwd, path);
    }
  }
}
